import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';

const HEARTBEAT_INTERVAL = 60000; // 60 seconds
const ACTIVITY_THROTTLE = 5000; // 5 seconds minimum between updates

/**
 * Activity Heartbeat Hook
 * 
 * Tracks user presence by updating last_activity_at timestamp in profiles table.
 * - Sends automatic heartbeat every 60 seconds
 * - Sends heartbeat on user activity (mouse, keyboard, scroll)
 * - Throttles to max 1 update per 5 seconds to prevent spam
 * 
 * Used for "Active Now" metrics on Team Management page.
 */
export const useActivityHeartbeat = () => {
 const { user } = useSupabaseAuth();
 const lastActivityRef = useRef<number>(0);
 const intervalRef = useRef<NodeJS.Timeout | null>(null);

 const sendHeartbeat = async () => {
 if (!user) return;

 const now = Date.now();
 if (now - lastActivityRef.current < ACTIVITY_THROTTLE) {
 return; // Throttle rapid calls
 }

 lastActivityRef.current = now;

 try {
 await supabase.rpc('update_user_activity');
 } catch (error) {
 console.error('Failed to send activity heartbeat:', error);
 }
 };

 useEffect(() => {
 if (!user) return;

 // Send initial heartbeat immediately
 sendHeartbeat();

 // Set up interval for regular heartbeats
 intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

 // Send heartbeat on user activity (mousemove, keypress, click, scroll)
 const activityEvents = ['mousemove', 'keypress', 'click', 'scroll'];
 activityEvents.forEach(event => {
 window.addEventListener(event, sendHeartbeat, { passive: true });
 });

 return () => {
 if (intervalRef.current) {
 clearInterval(intervalRef.current);
 }
 activityEvents.forEach(event => {
 window.removeEventListener(event, sendHeartbeat);
 });
 };
 }, [user]);
};
